import type { FC } from 'react';
import { useState } from 'react';

import { useAuth } from '@site-builder/block-helpers';

import * as styles from './CardButtons.css';
import { CartIcon } from '../../assets/icons/cart';
import { MinusIcon } from '../../assets/icons/minus';
import { PlusIcon } from '../../assets/icons/plus';
import { FormattedMessage, isItemPurchased } from '../../utils';
import { useStoreCard } from '../StoreCardWrapper/StoreCardContext';

import type { Item } from '@site-builder/store-sdk';

export const CardButtons: FC = () => {
  const {
    item,
    quantityInCart,
    availableAmount,
    onPurchase,
    onAddToCart,
    onInCartDecrement,
    onInCartIncrement,
  } = useStoreCard();
  const [{ isAuthorized }, { logIn }] = useAuth();
  const [isPurchasing, setIsPurchasing] = useState(false);

  const isPurchased = isItemPurchased(item as Item);
  const isDisabled = isPurchasing || isPurchased;

  const showAmountPicker = quantityInCart > 0;

  const getPriceLabel = () => {
    if (item.price) {
      const currencySymbol =
        item.price.currency === 'USD' ? '$' : item.price.currency;
      return `${currencySymbol}${item.price.amount}`;
    }
    return <FormattedMessage id='packs.no-price-available' />;
  };

  const handlePurchase = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!isAuthorized) {
      logIn();
      return;
    }

    if (!onPurchase || isPurchasing) {
      return;
    }

    setIsPurchasing(true);
    try {
      const result = onPurchase() as unknown;
      if (result instanceof Promise) {
        result.finally(() => setIsPurchasing(false));
      } else {
        setIsPurchasing(false);
      }
    } catch {
      setIsPurchasing(false);
    }
  };

  const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!isAuthorized) {
      logIn();
      return;
    }
    onAddToCart();
  };

  const handleDecrement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onInCartDecrement();
  };

  const handleIncrement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onInCartIncrement();
  };

  if (showAmountPicker && !isPurchased) {
    const canIncrement = quantityInCart < availableAmount;

    return (
      <div className={styles.buttonRow}>
        <div className={`msm-button ${styles.amountPicker}`}>
          <button
            type='button'
            onClick={handleDecrement}
            className={styles.amountAdjustButton}
          >
            <MinusIcon />
          </button>
          <span className={styles.amountCount}>{quantityInCart}</span>
          <button
            type='button'
            onClick={handleIncrement}
            disabled={!canIncrement}
            className={styles.amountAdjustButton}
          >
            <PlusIcon />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.buttonRow}>
      <button
        type='button'
        onClick={handlePurchase}
        disabled={isDisabled}
        className={`msm-button ${styles.button}`}
      >
        {isPurchasing ? (
          <FormattedMessage id='packs.loading' />
        ) : (
          getPriceLabel()
        )}
      </button>
      <button
        type='button'
        onClick={handleAddToCart}
        disabled={isDisabled}
        className={styles.cartButton}
      >
        <CartIcon />
      </button>
    </div>
  );
};
